import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { reportsAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';

const card = (label, value, icon, color, bg) => (
    <div style={{ background: bg, borderRadius: 12, padding: '18px 20px', border: `1px solid ${color}22`, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 30 }}>{icon}</div>
        <div>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{value ?? '—'}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{label}</div>
        </div>
    </div>
);

function getWeekRange() {
    const now = new Date();
    const day = now.getDay();
    const mon = new Date(now); mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: mon.toISOString().split('T')[0], to: sun.toISOString().split('T')[0] };
}

function getMonthRange() {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    return { from, to };
}

function printReport(html) {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return alert('Popup blocked! Please allow popups.');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 600);
}

export default function Reports() {
    const [tab, setTab] = useState('daily');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [from, setFrom] = useState(getWeekRange().from);
    const [to, setTo] = useState(getWeekRange().to);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            let res;
            if (tab === 'daily') {
                res = await reportsAPI.daily(date);
            } else {
                res = await reportsAPI.range(from, to);
            }
            setData(res.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load report');
            setData(null);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [tab]);

    const handleTabChange = (t) => {
        setData(null);
        setTab(t);
        if (t === 'weekly') { const r = getWeekRange(); setFrom(r.from); setTo(r.to); }
        else if (t === 'monthly') { const r = getMonthRange(); setFrom(r.from); setTo(r.to); }
    };

    const handlePrint = () => {
        if (!data) return;
        const title = tab === 'daily' ? `Daily Report — ${date}` : `Report: ${from} to ${to}`;
        const s = tab === 'daily' ? data.summary : data.summary;

        const summaryRows = tab === 'daily' ? [
            ['New Patients Registered', s.patients_new],
            ['OPD Visits', s.opd],
            ['IPD Admissions', s.ipd_new],
            ['IPD Discharges', s.ipd_discharge],
            ['Currently Admitted', s.ipd_current],
            ['ER Visits', s.er],
            ['Lab Orders', s.lab],
            ['Radiology Orders', s.pacs],
        ] : [
            ['New Patients Registered', s.patients_new],
            ['OPD Visits', s.opd],
            ['IPD Admissions', s.ipd_new],
            ['IPD Discharges', s.ipd_discharge],
            ['ER Visits', s.er],
            ['Lab Orders', s.lab],
            ['Radiology Orders', s.pacs],
        ];

        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
        <style>
            body { font-family: Arial, sans-serif; padding: 30px; color: #000; }
            h1 { font-size: 22px; color: #1e3a5f; margin-bottom: 4px; }
            h2 { font-size: 14px; color: #64748b; font-weight: normal; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
            th { background: #1e3a5f; color: white; padding: 10px 14px; text-align: left; font-size: 13px; }
            td { padding: 9px 14px; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) td { background: #f8fafc; }
            .section { font-size: 14px; font-weight: bold; color: #1e3a5f; margin: 18px 0 8px; }
            .footer { margin-top: 30px; font-size: 11px; color: #94a3b8; text-align: center; }
            @media print { @page { margin: 15mm; } }
        </style></head><body>
        <h1>🏥 HIMS — ${title}</h1>
        <h2>Generated: ${new Date().toLocaleString('en-PK')}</h2>
        <div class="section">Summary</div>
        <table>
            <tr><th>Module</th><th>Count</th></tr>
            ${summaryRows.map(([l, v]) => `<tr><td>${l}</td><td><strong>${v ?? 0}</strong></td></tr>`).join('')}
        </table>
        ${data.opd_by_dept?.length ? `
        <div class="section">OPD — Department Wise</div>
        <table>
            <tr><th>Department</th><th>Visits</th></tr>
            ${data.opd_by_dept.map(r => `<tr><td>${r.department || 'General'}</td><td>${r.count}</td></tr>`).join('')}
        </table>` : ''}
        ${data.top_diagnoses?.length ? `
        <div class="section">Top Diagnoses</div>
        <table>
            <tr><th>Diagnosis</th><th>Count</th></tr>
            ${data.top_diagnoses.map(r => `<tr><td>${r.diagnosis}</td><td>${r.count}</td></tr>`).join('')}
        </table>` : ''}
        <div class="footer">HIMS — Hospital Information Management System v2.0 — Confidential Hospital Record</div>
        </body></html>`;
        printReport(html);
    };

    // IPD Patients Report state
    const { token } = useAuthStore();
    const [ipdFrom, setIpdFrom] = useState(new Date().toISOString().split('T')[0]);
    const [ipdTo, setIpdTo] = useState(new Date().toISOString().split('T')[0]);
    const [ipdStatus, setIpdStatus] = useState('All');
    const [ipdData, setIpdData] = useState(null);
    const [ipdLoading, setIpdLoading] = useState(false);

    const loadIPDPatients = async () => {
        setIpdLoading(true);
        try {
            const res = await reportsAPI.ipdPatients(ipdFrom, ipdTo, ipdStatus);
            setIpdData(res.data);
        } catch(e) { toast.error('Failed to load IPD report'); }
        finally { setIpdLoading(false); }
    };

    const printIPDReport = () => {
        if (!ipdData) return;
        const statusBadge = { Admitted: '#065f46', Discharged: '#374151', All: '#1e3a5f' };
        const rows = ipdData.patients.map((p,i) => `
            <tr>
                <td>${i+1}</td>
                <td>${p.admission_number}</td>
                <td>${p.mrn}</td>
                <td><strong>${p.full_name}</strong></td>
                <td>${p.age||'—'} / ${p.gender||'—'}</td>
                <td>${p.ward_name} / Bed-${p.bed_number}</td>
                <td>${p.doctor_name||'—'}</td>
                <td>${p.admission_time ? new Date(p.admission_time).toLocaleDateString('en-PK') : '—'}</td>
                <td>${p.discharge_time ? new Date(p.discharge_time).toLocaleDateString('en-PK') : '—'}</td>
                <td>${p.los||'—'} day(s)</td>
                <td><span style="padding:2px 8px;border-radius:10px;font-size:11px;font-weight:700;background:${p.status==='Admitted'?'#dcfce7':'#f1f5f9'};color:${p.status==='Admitted'?'#065f46':'#374151'}">${p.status}</span></td>
            </tr>`).join('');
        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
            body{font-family:Arial,sans-serif;padding:24px;font-size:12px;}
            h1{font-size:18px;color:#1e3a5f;margin-bottom:2px;}
            h2{font-size:12px;color:#64748b;font-weight:normal;margin-bottom:16px;}
            table{width:100%;border-collapse:collapse;}
            th{background:#1e3a5f;color:white;padding:7px 10px;text-align:left;font-size:11px;}
            td{padding:6px 10px;border-bottom:1px solid #e2e8f0;font-size:11px;}
            tr:nth-child(even) td{background:#f8fafc;}
            .footer{margin-top:20px;font-size:10px;color:#94a3b8;text-align:center;}
            @media print{@page{size:A4 landscape;margin:10mm;}}
        </style></head><body>
        <h1>HIMS — IPD Patients Report</h1>
        <h2>Period: ${ipdFrom} to ${ipdTo} | Status: ${ipdStatus} | Total: ${ipdData.total} patients | Generated: ${new Date().toLocaleString('en-PK')}</h2>
        <table>
            <thead><tr><th>#</th><th>IPD#</th><th>MR#</th><th>Patient Name</th><th>Age/Gender</th><th>Ward/Bed</th><th>Consultant</th><th>Admission</th><th>Discharge</th><th>LOS</th><th>Status</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>
        <div class="footer">HIMS — IPD Patient Report | Confidential Hospital Record</div>
        </body></html>`;
        printReport(html);
    };

    const tabs = [
        { key: 'daily', label: '📅 Daily' },
        { key: 'weekly', label: '📆 Weekly' },
        { key: 'monthly', label: '🗓️ Monthly' },
        { key: 'custom', label: '⚙️ Custom' },
        { key: 'ipd', label: '🛏️ IPD Patients' },
    ];

    const s = data?.summary || {};

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>📊 Reports</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Hospital performance & statistics</p>
                </div>
                {data && (
                    <button onClick={handlePrint}
                        style={{ padding: '9px 22px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        🖨️ Print Report
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => handleTabChange(t.key)}
                        style={{ padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: tab === t.key ? '#1e3a5f' : '#f1f5f9',
                            color: tab === t.key ? 'white' : '#374151' }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Date Controls */}
            {tab !== 'ipd' && <div style={{ background: 'white', borderRadius: 10, padding: '14px 20px', border: '1px solid #e2e8f0', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
                {tab === 'daily' ? (
                    <>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Date:</label>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)}
                            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                    </>
                ) : (
                    <>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>From:</label>
                        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>To:</label>
                        <input type="date" value={to} onChange={e => setTo(e.target.value)}
                            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                    </>
                )}
                <button onClick={load} disabled={loading}
                    style={{ padding: '8px 20px', background: loading ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {loading ? 'Loading...' : '🔍 Load Report'}
                </button>
            </div>}

            {/* IPD Patients Tab */}
            {tab === 'ipd' && (
                <div>
                    <div style={{ background:'white', borderRadius:10, padding:'14px 20px', border:'1px solid #e2e8f0', marginBottom:20, display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                        <label style={{ fontSize:12, fontWeight:600, color:'#374151' }}>From:</label>
                        <input type="date" value={ipdFrom} onChange={e=>setIpdFrom(e.target.value)} style={{ padding:'7px 12px', border:'1px solid #d1d5db', borderRadius:7, fontSize:13 }} />
                        <label style={{ fontSize:12, fontWeight:600, color:'#374151' }}>To:</label>
                        <input type="date" value={ipdTo} onChange={e=>setIpdTo(e.target.value)} style={{ padding:'7px 12px', border:'1px solid #d1d5db', borderRadius:7, fontSize:13 }} />
                        <label style={{ fontSize:12, fontWeight:600, color:'#374151' }}>Status:</label>
                        <select value={ipdStatus} onChange={e=>setIpdStatus(e.target.value)} style={{ padding:'7px 12px', border:'1px solid #d1d5db', borderRadius:7, fontSize:13 }}>
                            <option>All</option>
                            <option>Admitted</option>
                            <option>Discharged</option>
                        </select>
                        <button onClick={loadIPDPatients} disabled={ipdLoading}
                            style={{ padding:'8px 20px', background:ipdLoading?'#9ca3af':'#059669', color:'white', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                            {ipdLoading ? 'Loading...' : '🔍 Load'}
                        </button>
                        {ipdData && <button onClick={printIPDReport}
                            style={{ padding:'8px 20px', background:'#1e3a5f', color:'white', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                            🖨️ Print Report
                        </button>}
                    </div>
                    {ipdLoading && <div style={{ textAlign:'center', padding:60, color:'#64748b' }}>Loading...</div>}
                    {ipdData && (
                        <div style={{ background:'white', borderRadius:10, border:'1px solid #e2e8f0', overflow:'hidden' }}>
                            <div style={{ padding:'12px 18px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <div style={{ fontWeight:700, fontSize:14, color:'#1e3a5f' }}>🛏️ IPD Patients — {ipdStatus}</div>
                                <div style={{ fontSize:12, color:'#64748b', fontWeight:600 }}>Total: {ipdData.total} patients</div>
                            </div>
                            <div style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                                    <thead><tr style={{ background:'#f8fafc' }}>
                                        {['#','IPD#','MR#','Patient Name','Age/Gender','Ward/Bed','Consultant','Admission Date','Discharge Date','LOS','Status'].map(h=>(
                                            <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#374151', whiteSpace:'nowrap' }}>{h}</th>
                                        ))}
                                    </tr></thead>
                                    <tbody>
                                        {ipdData.patients.length === 0 ? (
                                            <tr><td colSpan={11} style={{ padding:30, textAlign:'center', color:'#64748b' }}>No patients found for this period</td></tr>
                                        ) : ipdData.patients.map((p,i)=>(
                                            <tr key={p.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                                                <td style={{ padding:'8px 12px', fontSize:12 }}>{i+1}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12, fontWeight:600 }}>{p.admission_number}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12 }}>{p.mrn}</td>
                                                <td style={{ padding:'8px 12px', fontSize:13, fontWeight:700 }}>{p.full_name}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12 }}>{p.age||'—'} / {p.gender||'—'}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12 }}>{p.ward_name} / Bed-{p.bed_number}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12 }}>{p.doctor_name||'—'}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12, whiteSpace:'nowrap' }}>{p.admission_time ? new Date(p.admission_time).toLocaleDateString('en-PK') : '—'}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12, whiteSpace:'nowrap' }}>{p.discharge_time ? new Date(p.discharge_time).toLocaleDateString('en-PK') : '—'}</td>
                                                <td style={{ padding:'8px 12px', fontSize:12 }}>{p.los||'—'}d</td>
                                                <td style={{ padding:'8px 12px' }}>
                                                    <span style={{ padding:'3px 10px', borderRadius:10, fontSize:11, fontWeight:700, background:p.status==='Admitted'?'#dcfce7':'#f1f5f9', color:p.status==='Admitted'?'#065f46':'#374151' }}>{p.status}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Loading */}
            {tab !== 'ipd' && loading && <div style={{ textAlign: 'center', padding: 60, color: '#64748b', fontSize: 16 }}>Loading report...</div>}

            {/* Data */}
            {tab !== 'ipd' && !loading && data && (
                <div>
                    {/* Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                        {card('New Patients', s.patients_new, '👤', '#0369a1', '#eff6ff')}
                        {card('OPD Visits', s.opd, '🏥', '#1e3a5f', '#eff6ff')}
                        {card('IPD Admissions', s.ipd_new, '🛏️', '#065f46', '#f0fdf4')}
                        {card('ER Visits', s.er, '🚨', '#dc2626', '#fef2f2')}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                        {card('Lab Orders', s.lab, '🔬', '#7c3aed', '#faf5ff')}
                        {card('Radiology Orders', s.pacs, '🩻', '#0369a1', '#eff6ff')}
                        {card('IPD Discharges', s.ipd_discharge, '✅', '#059669', '#f0fdf4')}
                        {tab === 'daily' && card('Currently Admitted', s.ipd_current, '🏥', '#be123c', '#fff1f2')}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        {/* OPD by Department */}
                        {data.opd_by_dept?.length > 0 && (
                            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>
                                    🏥 OPD — Department Wise
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151' }}>Department</th>
                                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#374151' }}>Visits</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.opd_by_dept.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 14px', fontSize: 13 }}>{r.department || 'General'}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#1e3a5f' }}>{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* IPD Ward Wise (daily only) */}
                        {tab === 'daily' && data.ipd_by_ward?.length > 0 && (
                            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13, color: '#065f46' }}>
                                    🛏️ IPD — Ward Wise Admissions
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700 }}>Ward</th>
                                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700 }}>Admissions</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.ipd_by_ward.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 14px', fontSize: 13 }}>{r.ward}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#065f46' }}>{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ER Triage (daily) */}
                        {tab === 'daily' && data.er_by_triage?.length > 0 && (
                            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13, color: '#dc2626' }}>
                                    🚨 ER — Triage Levels
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700 }}>Triage Level</th>
                                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700 }}>Patients</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.er_by_triage.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 14px', fontSize: 13 }}>{r.triage_level || 'Unknown'}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#dc2626' }}>{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Top Diagnoses (range) */}
                        {data.top_diagnoses?.length > 0 && (
                            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13, color: '#7c3aed' }}>
                                    🩺 Top Diagnoses
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700 }}>Diagnosis</th>
                                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700 }}>Count</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.top_diagnoses.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 14px', fontSize: 13 }}>{r.diagnosis}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#7c3aed' }}>{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* No data message */}
                    {s.opd === 0 && s.ipd_new === 0 && s.er === 0 && (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', marginTop: 16 }}>
                            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                            <p style={{ fontWeight: 600 }}>No data found for this date</p>
                            <p style={{ fontSize: 12 }}>Try a different date or check if patients have been registered</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
